/**
 * BotSpot Web Dashboard - useLogs Hook
 *
 * React hook for fetching and auto-updating bot logs.
 */

import { useEffect, useState } from "react";
import { getBotLogs } from "../api/bots";
import { getErrorMessage } from "../api/client";
import type { LogList } from "../api/types";

/**
 * Hook options
 */
interface UseLogsOptions {
  /**
   * Number of recent log lines to fetch (default: 200)
   */
  lines?: number;
  /**
   * Polling interval in milliseconds (default: 5000ms = 5s)
   */
  pollInterval?: number;
  /**
   * Whether to enable automatic polling (default: true)
   */
  enablePolling?: boolean;
}

/**
 * Hook return value
 */
interface UseLogsReturn {
  logs: LogList | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * useLogs Hook
 *
 * Fetches bot logs and optionally polls for updates.
 *
 * @param botId - Bot ID to fetch logs for
 * @param options - Hook configuration options
 * @returns Log data, loading state, error, and refresh function
 */
export function useLogs(
  botId: string | undefined,
  options: UseLogsOptions = {}
): UseLogsReturn {
  const {
    lines = 200,
    pollInterval = 5000,
    enablePolling = true,
  } = options;

  const [logs, setLogs] = useState<LogList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch logs from API
   */
  const fetchLogs = async () => {
    if (!botId) {
      setError("Bot ID is required");
      setLoading(false);
      return;
    }

    try {
      const data = await getBotLogs(botId, { lines });
      setLogs(data);
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Refresh logs (callable from component)
   */
  const refresh = async () => {
    setLoading(true);
    await fetchLogs();
  };

  /**
   * Initial fetch and polling setup
   */
  useEffect(() => {
    if (!botId) {
      setLoading(false);
      return;
    }

    // Initial fetch
    fetchLogs();

    // Set up polling if enabled
    if (enablePolling) {
      const interval = setInterval(() => {
        if (botId) {
          getBotLogs(botId, { lines })
            .then((data) => {
              setLogs(data);
              setError(null);
            })
            .catch((err) => {
              // Silent fail on polling errors to avoid UI disruption
              console.error("Logs polling error:", err);
            });
        }
      }, pollInterval);

      return () => clearInterval(interval);
    }
  }, [botId, lines, pollInterval, enablePolling]);

  return {
    logs,
    loading,
    error,
    refresh,
  };
}
